import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, Typography, Button, Alert, Spinner, Breadcrumbs, Pagination, Stepper, Chip, CardActionArea } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import {
  vendorApiClient,
  type ImportOutcome,
  type ImportResultRow,
  type ImportRowsResponse,
  type ImportStatus,
  type VendorLocation,
} from '../../lib/vendorApiClient';
import { fixCsv } from '../../lib/importFlow';
import { BORDER, GREEN_INK, HEADING, MUTED, ORANGE_INK, RED, PageTitle, ProductStatusChip, describeProductError } from './parts';

/**
 * P8 — the import, while it works and when it is finished (Figma
 * `fVTA2OnSEz4UOdTeoXeByH` 137:8287).
 *
 * Three numbers: what shoppers can see now, what is waiting on the vendor,
 * and what we could not read. The page can be left and come back to — the
 * work does not depend on it being open.
 */

const POLL_MS = 2000;
const PAGE_SIZE = 10;

const TONE: Record<ImportOutcome, string> = { live: GREEN_INK, needs_answer: ORANGE_INK, unreadable: RED };

/** Why a row was sent back, in the reader's language, quoting their own cell where that helps. */
export function problemText(t: TFunction, problem: { code: string; value?: string } | null): string {
  if (!problem) return '';
  return t(`products.import.problem.${problem.code}`, { value: problem.value ?? '', defaultValue: t('products.import.problem.internal') });
}

function place(t: TFunction, row: { file: string | null; sheet: string | null; rowNumber: number }): string {
  const at = t('products.questions.place.row', { row: row.rowNumber });
  if (row.sheet) return `${t('products.questions.place.tab', { tab: row.sheet })} · ${at}`;
  return row.file ? `${row.file} · ${at}` : at;
}

export default function ImportResults() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { batchId = '' } = useParams();
  const { activeVendorId } = useVendor();
  const backupFailed = Boolean((useLocation().state as { backupFailed?: boolean } | null)?.backupFailed);

  const [status, setStatus] = useState<ImportStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ImportRowsResponse | null>(null);
  const [pending, setPending] = useState<VendorLocation[]>([]);
  const [downloading, setDownloading] = useState(false);

  // ── Progress: ask every couple of seconds until it is done ─────
  useEffect(() => {
    if (!activeVendorId || !batchId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = () => {
      vendorApiClient
        .getImport(activeVendorId, batchId)
        .then((s) => {
          if (!alive) return;
          setStatus(s);
          setError(null);
          if (s.status === 'working' || s.status === 'uploading') timer = setTimeout(ask, POLL_MS);
        })
        .catch((e: Error) => {
          if (!alive) return;
          setError(describeProductError(e, t));
        });
    };
    ask();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [activeVendorId, batchId, t]);

  const done = status?.status === 'done';

  // Prices saved for a location we are still verifying show once it is approved.
  useEffect(() => {
    if (!activeVendorId || !done) return;
    let alive = true;
    vendorApiClient
      .listLocations(activeVendorId)
      .then((r) => alive && setPending(r.items.filter((l) => l.status !== 'live')))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [activeVendorId, done]);

  // Start on what needs doing: rows to fix, then questions, then what went live.
  useEffect(() => {
    if (!done || !status || outcome) return;
    setOutcome(status.unreadable > 0 ? 'unreadable' : status.needsAnswer > 0 ? 'needs_answer' : 'live');
  }, [done, status, outcome]);

  const seq = useRef(0);
  const loadRows = useCallback(() => {
    if (!activeVendorId || !done || !outcome) return;
    const mine = ++seq.current;
    vendorApiClient
      .listImportRows(activeVendorId, batchId, { outcome, page, pageSize: PAGE_SIZE })
      .then((r) => mine === seq.current && setRows(r))
      .catch((e: Error) => mine === seq.current && setError(describeProductError(e, t)));
  }, [activeVendorId, batchId, done, outcome, page, t]);
  useEffect(loadRows, [loadRows]);

  const downloadFix = async () => {
    if (!activeVendorId || !status) return;
    setDownloading(true);
    try {
      const all = await vendorApiClient.listImportRows(activeVendorId, batchId, { outcome: 'unreadable', pageSize: 500, cells: true });
      const csv = fixCsv(
        all.items.map((r) => ({ sheetIndex: r.sheetIndex, file: r.file, sheet: r.sheet, rowNumber: r.rowNumber, reason: problemText(t, r.problem), cells: r.cells ?? [] })),
        all.headers ?? [],
        { reason: t('products.import.fix.reason'), tab: t('products.import.fix.tab'), row: t('products.import.fix.row') },
      );
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = t('products.import.fix.fileName');
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setDownloading(false);
    }
  };

  const crumbs = (
    <>
      <Breadcrumbs
        ariaLabel={t('products.detail.breadcrumbAria')}
        linkComponent={Link}
        items={[
          { label: t('nav.products'), to: '/products' },
          { label: t('products.add.title'), to: '/products/add' },
          { label: t('products.import.crumb'), ariaCurrent: true },
        ]}
      />
      {backupFailed && (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="import-backup-failed">
          {t('products.import.backupFailed')}
        </Alert>
      )}
    </>
  );
  const steps = (active: number) => (
    <Stepper
      ariaLabel={t('products.import.steps.aria')}
      color="secondary"
      activeStep={active}
      allowFutureNavigation={false}
      steps={[
        { key: 'upload', label: t('products.import.steps.upload'), disabled: true },
        { key: 'match', label: t('products.import.steps.match'), disabled: true },
        { key: 'import', label: t('products.import.steps.import'), disabled: true },
      ]}
      sx={{ mt: 3, maxWidth: 720 }}
    />
  );

  if (!status) {
    return (
      <Box>
        {crumbs}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Spinner color="brand" />
          </Box>
        )}
      </Box>
    );
  }

  const fileLine = [
    status.files.length === 1 ? status.files[0] : t('products.import.fileCount', { count: status.files.length }),
    t('products.import.upload.rows', { count: status.total, rows: status.total.toLocaleString() }),
  ].join(' · ');

  if (!done) {
    const failed = status.status === 'failed';
    return (
      <Box>
        {crumbs}
        <Box sx={{ mt: 2 }}>
          <PageTitle title={t(failed ? 'products.import.failed.title' : 'products.import.working.title')} subtitle={fileLine} />
        </Box>
        {steps(2)}
        {failed ? (
          <Alert severity="error" sx={{ mt: 3 }} data-testid="import-failed">
            {t('products.import.failed.body')}
          </Alert>
        ) : (
          <Box sx={{ mt: 3, p: 4, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px' }} role="status" data-testid="import-working">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Spinner color="brand" />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: HEADING }}>
                {t('products.import.working.progress', { done: status.processed.toLocaleString(), total: status.total.toLocaleString() })}
              </Typography>
            </Box>
            <Box
              role="progressbar"
              aria-label={t('products.import.working.title')}
              aria-valuemin={0}
              aria-valuemax={status.total}
              aria-valuenow={status.processed}
              sx={{ mt: 2, height: 8, borderRadius: 4, bgcolor: '#E9EBEF', overflow: 'hidden' }}
            >
              <Box sx={{ height: '100%', width: `${status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0}%`, bgcolor: '#3DA026', transition: 'width 0.4s' }} />
            </Box>
            <Typography sx={{ mt: 2, fontSize: 14, color: MUTED }}>{t('products.import.working.leave')}</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  const totalPages = rows ? Math.max(1, Math.ceil(rows.total / rows.pageSize)) : 1;
  const counts: { key: ImportOutcome; count: number }[] = [
    { key: 'live', count: status.live },
    { key: 'needs_answer', count: status.needsAnswer },
    { key: 'unreadable', count: status.unreadable },
  ];

  return (
    <Box>
      {crumbs}
      <Box sx={{ mt: 2 }}>
        <PageTitle title={t('products.import.done.title')} subtitle={fileLine} />
      </Box>
      {steps(3)}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {counts.map(({ key, count }) => {
          const selected = outcome === key;
          return (
            <Box key={key} sx={{ bgcolor: '#fff', border: selected ? `2px solid ${TONE[key]}` : `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden' }}>
              <CardActionArea
                aria-pressed={selected}
                onClick={() => {
                  setOutcome(key);
                  setPage(1);
                  setRows(null);
                }}
                data-testid={`import-count-${key}`}
                sx={{ p: 2.5, height: '100%', display: 'block', textAlign: 'left' }}
              >
                <Typography sx={{ fontSize: 40, lineHeight: 1.1, fontWeight: 700, color: TONE[key] }}>{count.toLocaleString()}</Typography>
                <Typography sx={{ mt: 0.5, fontSize: 17, fontWeight: 600, color: HEADING }}>{t(`products.import.done.${key}.title`)}</Typography>
                <Typography sx={{ fontSize: 14, color: MUTED }}>{t(`products.import.done.${key}.hint`)}</Typography>
              </CardActionArea>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 3, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px' }} data-testid="import-rows">
        {!rows ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Spinner color="brand" />
          </Box>
        ) : rows.items.length === 0 ? (
          <Typography sx={{ p: 3, fontSize: 15, color: MUTED }}>{t(`products.import.done.${outcome ?? 'live'}.none`)}</Typography>
        ) : (
          rows.items.map((row, i) => <ResultRow key={row.rowId} row={row} outcome={outcome ?? 'live'} first={i === 0} />)
        )}
      </Box>

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ flex: 1, minWidth: 240, fontSize: 14, color: MUTED }} data-testid="import-rows-range">
          {rows && rows.total > 0 ? t('products.import.done.showing', { shown: rows.items.length, count: rows.total }) : ''}
          {pending.length > 0 ? ` ${t('products.import.done.pendingLocations', { locations: pending.map((l) => l.name).join(', '), count: pending.length })}` : ''}
        </Typography>
        {totalPages > 1 && rows && (
          <Pagination page={rows.page} totalPages={totalPages} onPageChange={setPage} ariaLabel={t('products.import.done.pagination')} activeBgColor="#25862D" activeColor="#FFFFFF" align="right" />
        )}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Button
          ariaLabel={t('products.import.done.another')}
          buttonText={t('products.import.done.another')}
          variant="text"
          onClick={() => void navigate('/products/add/import')}
          dataTestId="import-another"
        />
        {status.unreadable > 0 && (
          <Button
            ariaLabel={t('products.import.done.download', { count: status.unreadable })}
            buttonText={t('products.import.done.download', { count: status.unreadable })}
            variant="outlinedGreen"
            disabled={downloading}
            onClick={() => void downloadFix()}
            dataTestId="import-download"
          />
        )}
        {status.needsAnswer > 0 ? (
          <Button
            ariaLabel={t('products.import.done.seeQuestions', { count: status.needsAnswer })}
            buttonText={t('products.import.done.seeQuestions', { count: status.needsAnswer })}
            variant="contained"
            color="primary"
            onClick={() => void navigate('/products?status=needs_answer')}
            dataTestId="import-see-questions"
          />
        ) : (
          <Button
            ariaLabel={t('products.import.done.seeProducts')}
            buttonText={t('products.import.done.seeProducts')}
            variant="contained"
            color="primary"
            onClick={() => void navigate('/products')}
            dataTestId="import-see-products"
          />
        )}
      </Box>
    </Box>
  );
}

function ResultRow({ row, outcome, first }: { row: ImportResultRow; outcome: ImportOutcome; first: boolean }) {
  const { t } = useTranslation();
  const title = outcome === 'unreadable' && !row.name ? t('products.import.done.rowTitle', { row: row.rowNumber }) : [row.name, row.size].filter(Boolean).join(' · ');
  return (
    <Box
      data-testid={`import-row-${row.rowId}`}
      sx={{ px: 3, py: 2, borderTop: first ? 'none' : `1px solid ${BORDER}`, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 1.5fr) 170px 70px' }, gap: { xs: 0.75, md: 2 }, alignItems: 'center' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING, overflowWrap: 'anywhere' }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: MUTED }}>{place(t, row)}</Typography>
      </Box>
      <Typography sx={{ fontSize: 15, color: outcome === 'unreadable' ? RED : '#343434' }}>
        {outcome === 'unreadable' ? problemText(t, row.problem) : t(`products.import.done.${outcome}.rowNote`)}
      </Typography>
      {outcome === 'unreadable' ? (
        <StatusPill text={t('products.import.done.needsFix')} />
      ) : (
        <ProductStatusChip status={outcome === 'live' ? 'live' : row.blockState === 'handed_to_glp' ? 'with_us' : row.blockState === 'unresolvable' ? 'stuck' : 'needs_answer'} />
      )}
      {row.vendorProductId ? (
        <Box
          component={Link}
          to={`/products/${row.vendorProductId}`}
          aria-label={t(outcome === 'needs_answer' ? 'products.import.done.answerAria' : 'products.import.done.viewAria', { name: row.name })}
          sx={{ fontSize: 15, fontWeight: 700, color: GREEN_INK, textDecoration: 'none', justifySelf: { md: 'end' } }}
        >
          {t(outcome === 'needs_answer' ? 'products.add.result.answer' : 'products.import.done.view')}
        </Box>
      ) : (
        <Box />
      )}
    </Box>
  );
}

function StatusPill({ text }: { text: string }) {
  return <Chip label={`● ${text}`} size="small" backgroundColor="#FFFFFF" textColor={RED} fontSize={12} fontWeight={700} sx={{ border: `1px solid ${RED}`, justifySelf: 'start' }} />;
}
